/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOktaResourceConnectionsRequest,
  ListOktaResourceConnectionsSecurity,
} from "../models/operations/listoktaresourceconnections.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOktaResourceConnectionsQuery,
  OktaResourceConnectionsQueryData,
  prefetchOktaResourceConnections,
  queryKeyOktaResourceConnections,
} from "./oktaResourceConnections.core.js";
export {
  buildOktaResourceConnectionsQuery,
  type OktaResourceConnectionsQueryData,
  prefetchOktaResourceConnections,
  queryKeyOktaResourceConnections,
};

export type OktaResourceConnectionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list oktaResourceConnections
 *
 * @remarks
 * List the Okta resource connection and derived state of every eligible MCP server. Requires org:admin and a live identity provider connection.
 */
export function useOktaResourceConnections(
  request?: ListOktaResourceConnectionsRequest | undefined,
  security?: ListOktaResourceConnectionsSecurity | undefined,
  options?: QueryHookOptions<
    OktaResourceConnectionsQueryData,
    OktaResourceConnectionsQueryError
  >,
): UseQueryResult<
  OktaResourceConnectionsQueryData,
  OktaResourceConnectionsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOktaResourceConnectionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list oktaResourceConnections
 *
 * @remarks
 * List the Okta resource connection and derived state of every eligible MCP server. Requires org:admin and a live identity provider connection.
 */
export function useOktaResourceConnectionsSuspense(
  request?: ListOktaResourceConnectionsRequest | undefined,
  security?: ListOktaResourceConnectionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OktaResourceConnectionsQueryData,
    OktaResourceConnectionsQueryError
  >,
): UseSuspenseQueryResult<
  OktaResourceConnectionsQueryData,
  OktaResourceConnectionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOktaResourceConnectionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOktaResourceConnectionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      includeAll?: boolean | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: OktaResourceConnectionsQueryData,
): OktaResourceConnectionsQueryData | undefined {
  const key = queryKeyOktaResourceConnections(...queryKeyBase);

  return client.setQueryData<OktaResourceConnectionsQueryData>(key, data);
}

export function invalidateOktaResourceConnections(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      includeAll?: boolean | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "oktaResourceConnections",
      "list",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOktaResourceConnections(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "oktaResourceConnections", "list"],
  });
}
